/**
 * Per-region stylized map configs for the LocationMapVisual component
 * rendered on the seven location landing pages.
 *
 * Coordinate system: 400 x 280 viewBox. Layouts are roughly geographically
 * suggestive (not anatomically accurate): emphasis is on a "live ops view"
 * feel: districts, animated pins, HQ glow, and dashed client routes.
 *
 * Per .cursor/rules/app-mockup-visuals.mdc, this is HTML/CSS imagery.
 */

import { ShieldCheck, MapPin, Activity, Brain, Sparkles, Cpu } from "lucide-react";

/* -------- Greater Washington (DC + NoVA + MD metro) -------- */
export const dcMapConfig = {
  id: "dc",
  region: "Greater Washington",
  subtitle: "DC · NoVA · MD metro",
  searchPlaceholder: "Washington DC + Beltway",
  accentColor: "#00AEEF",
  // Potomac + Anacostia rivers (stylized)
  waterAreas: [
    { d: "M150,210 Q180,180 220,200 Q260,220 290,250 L260,280 L160,280 Z", opacity: 0.08 },
  ],
  // I-495 Beltway approximation + I-66
  roads: [
    "M120,80 Q60,160 120,240 Q200,290 290,240 Q360,160 290,80 Q200,40 120,80 Z",
    "M0,140 L400,140",
    "M210,0 L210,280",
  ],
  districts: [
    { x: 70, y: 110, text: "Reston" },
    { x: 110, y: 130, text: "Tysons" },
    { x: 140, y: 80, text: "Bethesda" },
    { x: 230, y: 75, text: "Silver Spring" },
    { x: 160, y: 195, text: "Arlington" },
    { x: 215, y: 150, text: "Washington DC", bold: true },
    { x: 170, y: 240, text: "Alexandria" },
  ],
  pins: [
    { x: 215, y: 145, type: "hq", label: "Maxiom HQ" },
    { x: 95, y: 115, type: "client", label: "Reston" },
    { x: 150, y: 90, type: "client", label: "Bethesda" },
    { x: 175, y: 190, type: "client", label: "Arlington" },
    { x: 120, y: 135, type: "client" },
    { x: 245, y: 80, type: "client" },
  ],
  stats: [
    { value: "120+", label: "DMV clients" },
    { value: "25 yr", label: "In metro" },
    { value: "85+", label: "Local team" },
    { value: "Clr.", label: "Engineers ready" },
  ],
  recentClients: [
    "AT&T Government Solutions",
    "FiscalNote",
    "PAN Foundation",
    "Toastmasters International",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "FedRAMP-ready",
      subtitle: "Cleared engineers",
    },
    {
      Icon: MapPin,
      title: "On-site DMV",
      subtitle: "DC · VA · MD weekly",
    },
  ],
};

/* -------- Virginia (state-wide) -------- */
export const vaMapConfig = {
  id: "va",
  region: "Virginia",
  subtitle: "NoVA · Richmond · Hampton Roads",
  searchPlaceholder: "Virginia · Reston → Norfolk",
  accentColor: "#10B981",
  waterAreas: [
    { d: "M320,200 Q360,210 380,250 L380,280 L300,280 Z", opacity: 0.08 },
  ],
  roads: [
    // I-95 corridor (north-south)
    "M150,40 Q170,120 200,200 T240,280",
    // I-64 (east-west across state)
    "M50,180 Q150,200 250,210 T390,230",
    // I-66 (west to NoVA)
    "M40,90 Q90,95 150,100",
  ],
  districts: [
    { x: 70, y: 105, text: "Reston" },
    { x: 130, y: 95, text: "Tysons", bold: true },
    { x: 180, y: 105, text: "Arlington" },
    { x: 90, y: 195, text: "Charlottesville" },
    { x: 60, y: 230, text: "Roanoke" },
    { x: 215, y: 215, text: "Richmond", bold: true },
    { x: 320, y: 245, text: "Norfolk" },
  ],
  pins: [
    { x: 135, y: 90, type: "hq", label: "NoVA HQ" },
    { x: 75, y: 100, type: "client", label: "Reston" },
    { x: 185, y: 100, type: "client" },
    { x: 220, y: 210, type: "client", label: "Richmond" },
    { x: 330, y: 240, type: "client" },
    { x: 95, y: 190, type: "client" },
  ],
  stats: [
    { value: "60+", label: "VA clients" },
    { value: "20 yr", label: "On the ground" },
    { value: "40+", label: "Local engineers" },
    { value: "DoD", label: "Workflows" },
  ],
  recentClients: [
    "AT&T Government Solutions",
    "FiscalNote",
    "SCS Engineers",
    "Open Tech Alliance",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Defense-fluent",
      subtitle: "Clearable engineers",
    },
    {
      Icon: MapPin,
      title: "Reston → Richmond",
      subtitle: "On-site partnership",
    },
  ],
};

/* -------- Maryland (state-wide) -------- */
export const mdMapConfig = {
  id: "md",
  region: "Maryland",
  subtitle: "Bethesda · Columbia · Baltimore",
  searchPlaceholder: "Maryland · DC suburbs → Baltimore",
  accentColor: "#F59E0B",
  waterAreas: [
    // Chesapeake Bay
    { d: "M260,40 Q290,130 280,220 Q260,260 220,260 Q240,180 260,40 Z", opacity: 0.1 },
  ],
  roads: [
    // I-95 northeast corridor
    "M80,170 Q150,140 220,110 T370,40",
    // I-270 (DC → Frederick)
    "M100,210 Q90,140 80,70",
    // I-695 Baltimore beltway
    "M210,60 Q260,90 230,140 Q180,150 200,90 Z",
  ],
  districts: [
    { x: 60, y: 65, text: "Frederick" },
    { x: 80, y: 130, text: "Rockville" },
    { x: 120, y: 215, text: "Bethesda", bold: true },
    { x: 175, y: 170, text: "Silver Spring" },
    { x: 175, y: 115, text: "Columbia" },
    { x: 215, y: 75, text: "Baltimore", bold: true },
    { x: 230, y: 195, text: "Annapolis" },
  ],
  pins: [
    { x: 125, y: 210, type: "hq", label: "Bethesda HQ" },
    { x: 175, y: 115, type: "client", label: "Columbia" },
    { x: 215, y: 75, type: "client", label: "Baltimore" },
    { x: 80, y: 135, type: "client" },
    { x: 60, y: 70, type: "client" },
    { x: 230, y: 195, type: "client" },
  ],
  stats: [
    { value: "45+", label: "MD clients" },
    { value: "HIPAA", label: "Engineering" },
    { value: "30+", label: "Local team" },
    { value: "NIH", label: "Adjacent work" },
  ],
  recentClients: [
    "PAN Foundation",
    "SmileML",
    "American Water Works Assoc.",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: Activity,
      title: "HIPAA · HITRUST",
      subtitle: "Biotech & healthcare",
    },
    {
      Icon: MapPin,
      title: "Bethesda + Columbia",
      subtitle: "Weekly on-site",
    },
  ],
};

/* -------- New York City (boroughs) -------- */
export const nyMapConfig = {
  id: "ny",
  region: "New York City",
  subtitle: "Manhattan · Brooklyn · Queens · LIC",
  searchPlaceholder: "NYC · Manhattan + boroughs",
  accentColor: "#EC4899",
  waterAreas: [
    // East River
    { d: "M225,0 Q210,80 218,160 Q230,220 215,280 L240,280 Q255,220 240,160 Q230,80 245,0 Z", opacity: 0.09 },
    // Hudson
    { d: "M120,0 Q105,140 110,280 L80,280 Q90,140 100,0 Z", opacity: 0.07 },
  ],
  roads: [
    // FDR / West Side Hwy stylized
    "M160,0 L160,280",
    "M195,0 L195,280",
    // Avenues
    "M0,90 L400,90",
    "M0,180 L400,180",
  ],
  districts: [
    { x: 175, y: 70, text: "UWS" },
    { x: 175, y: 130, text: "Midtown", bold: true },
    { x: 175, y: 175, text: "SoHo" },
    { x: 170, y: 220, text: "FiDi" },
    { x: 260, y: 80, text: "Astoria" },
    { x: 260, y: 130, text: "LIC" },
    { x: 285, y: 175, text: "Williamsburg" },
    { x: 295, y: 230, text: "DUMBO" },
  ],
  pins: [
    { x: 185, y: 125, type: "hq", label: "Midtown" },
    { x: 180, y: 215, type: "client", label: "FiDi" },
    { x: 270, y: 170, type: "client", label: "Williamsburg" },
    { x: 260, y: 120, type: "client" },
    { x: 275, y: 80, type: "client" },
    { x: 185, y: 175, type: "client" },
  ],
  stats: [
    { value: "70+", label: "NYC clients" },
    { value: "Fin.", label: "Fluent teams" },
    { value: "30+", label: "Local engineers" },
    { value: "24/7", label: "Coverage" },
  ],
  recentClients: [
    "FiscalNote",
    "Online Rewards",
    "Hyperlogs",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "Fintech + media",
      subtitle: "Compliance fluent",
    },
    {
      Icon: MapPin,
      title: "Manhattan + LIC",
      subtitle: "On-site every sprint",
    },
  ],
};

/* -------- Greater Boston (Cambridge / Boston / Waltham) -------- */
export const bostonMapConfig = {
  id: "boston",
  region: "Greater Boston",
  subtitle: "Cambridge · Boston · Waltham",
  searchPlaceholder: "Greater Boston · MIT → Seaport",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Charles River
    { d: "M40,150 Q150,135 220,165 Q280,195 360,200 L360,220 Q280,215 220,185 Q150,155 40,170 Z", opacity: 0.09 },
    // Harbor
    { d: "M310,160 Q360,170 380,260 L300,260 Z", opacity: 0.07 },
  ],
  roads: [
    // I-90 (Mass Pike)
    "M40,180 Q150,170 250,180 T390,180",
    // I-93
    "M250,40 Q245,160 260,280",
    // Storrow / Memorial Drive
    "M50,140 Q150,130 250,145",
  ],
  districts: [
    { x: 60, y: 130, text: "Waltham" },
    { x: 165, y: 105, text: "Somerville" },
    { x: 175, y: 145, text: "Cambridge", bold: true },
    { x: 200, y: 140, text: "Kendall Sq." },
    { x: 245, y: 190, text: "Back Bay" },
    { x: 275, y: 210, text: "Seaport", bold: true },
    { x: 215, y: 235, text: "Brookline" },
  ],
  pins: [
    { x: 200, y: 140, type: "hq", label: "Kendall Sq." },
    { x: 250, y: 190, type: "client", label: "Back Bay" },
    { x: 280, y: 210, type: "client", label: "Seaport" },
    { x: 65, y: 135, type: "client" },
    { x: 170, y: 110, type: "client" },
    { x: 215, y: 235, type: "client" },
  ],
  stats: [
    { value: "40+", label: "Boston clients" },
    { value: "Bio.", label: "Tech fluent" },
    { value: "20+", label: "Local engineers" },
    { value: "MIT", label: "Adjacent" },
  ],
  recentClients: [
    "SmileML",
    "Toastmasters International",
    "TruthQuotient",
    "BrokerBlocks",
  ],
  badges: [
    {
      Icon: Brain,
      title: "Biotech + EdTech",
      subtitle: "HIPAA · HITRUST · FERPA",
    },
    {
      Icon: MapPin,
      title: "Cambridge + Boston",
      subtitle: "Walking distance",
    },
  ],
};

/* -------- Austin -------- */
export const austinMapConfig = {
  id: "austin",
  region: "Austin",
  subtitle: "Downtown · East Side · Round Rock",
  searchPlaceholder: "Austin · I-35 corridor",
  accentColor: "#F59E0B",
  waterAreas: [
    // Lady Bird Lake (stylized)
    { d: "M120,170 Q190,160 260,175 Q280,185 250,195 Q190,190 130,185 Z", opacity: 0.1 },
  ],
  roads: [
    // I-35
    "M210,0 L210,280",
    // MoPac
    "M120,0 Q140,120 130,260",
    // 290 / 71
    "M0,115 L400,115",
  ],
  districts: [
    { x: 145, y: 55, text: "Cedar Park" },
    { x: 215, y: 55, text: "Round Rock" },
    { x: 145, y: 95, text: "Domain" },
    { x: 245, y: 95, text: "Pflugerville" },
    { x: 165, y: 160, text: "Downtown", bold: true },
    { x: 260, y: 160, text: "East Side", bold: true },
    { x: 175, y: 215, text: "South Congress" },
    { x: 260, y: 220, text: "Mueller" },
  ],
  pins: [
    { x: 190, y: 155, type: "hq", label: "Downtown" },
    { x: 255, y: 155, type: "client", label: "East Side" },
    { x: 215, y: 60, type: "client", label: "Round Rock" },
    { x: 145, y: 95, type: "client" },
    { x: 185, y: 215, type: "client" },
  ],
  stats: [
    { value: "35+", label: "Austin clients" },
    { value: "SaaS", label: "Fluent stack" },
    { value: "18+", label: "Local engineers" },
    { value: "TX", label: "Speed of ship" },
  ],
  recentClients: [
    "Hyperlogs",
    "CrowdKeep",
    "Online Rewards",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "SaaS + IoT fluent",
      subtitle: "From MVP to scale",
    },
    {
      Icon: MapPin,
      title: "Downtown + East Side",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Los Angeles -------- */
export const laMapConfig = {
  id: "la",
  region: "Greater Los Angeles",
  subtitle: "Santa Monica · DTLA · West Side",
  searchPlaceholder: "LA · West Side → DTLA",
  accentColor: "#EC4899",
  waterAreas: [
    // Pacific (vertical band on the left)
    { d: "M0,0 L70,0 Q55,140 60,280 L0,280 Z", opacity: 0.08 },
  ],
  roads: [
    // I-405
    "M120,0 Q145,120 130,280",
    // I-10
    "M0,170 L400,170",
    // US-101 / I-110
    "M250,0 Q245,140 260,280",
    // I-5
    "M340,0 L320,280",
  ],
  districts: [
    { x: 85, y: 90, text: "Malibu" },
    { x: 85, y: 165, text: "Santa Monica", bold: true },
    { x: 150, y: 200, text: "Culver City" },
    { x: 185, y: 140, text: "West Hollywood" },
    { x: 245, y: 110, text: "Burbank" },
    { x: 295, y: 130, text: "Pasadena" },
    { x: 255, y: 195, text: "DTLA", bold: true },
    { x: 270, y: 250, text: "Long Beach" },
  ],
  pins: [
    { x: 95, y: 160, type: "hq", label: "Santa Monica" },
    { x: 260, y: 190, type: "client", label: "DTLA" },
    { x: 155, y: 195, type: "client", label: "Culver City" },
    { x: 250, y: 105, type: "client" },
    { x: 295, y: 125, type: "client" },
    { x: 190, y: 140, type: "client" },
  ],
  stats: [
    { value: "50+", label: "LA clients" },
    { value: "D2C", label: "+ media fluent" },
    { value: "25+", label: "Local engineers" },
    { value: "PT", label: "Coast aligned" },
  ],
  recentClients: [
    "BMW Performance Center",
    "Microsoft",
    "Online Rewards",
    "Open Tech Alliance",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "Media + D2C",
      subtitle: "Entertainment fluent",
    },
    {
      Icon: MapPin,
      title: "Santa Monica + DTLA",
      subtitle: "PT-aligned coverage",
    },
  ],
};

/* -------- San Francisco / Bay Area -------- */
export const sfMapConfig = {
  id: "sf",
  region: "San Francisco Bay Area",
  subtitle: "SoMa · FiDi · Peninsula · Oakland",
  searchPlaceholder: "Bay Area · SF + Peninsula",
  accentColor: "#00AEEF",
  waterAreas: [
    // Pacific Ocean (left edge)
    { d: "M0,0 L60,0 Q50,140 55,280 L0,280 Z", opacity: 0.08 },
    // San Francisco Bay (central body)
    { d: "M180,40 Q210,100 200,180 Q190,240 220,280 L260,280 Q250,220 245,160 Q240,90 230,40 Z", opacity: 0.1 },
  ],
  roads: [
    // US-101 (peninsula corridor, north-south)
    "M150,0 Q160,140 170,280",
    // I-280 (parallel inland)
    "M110,40 Q125,150 140,280",
    // Bay Bridge / I-80 (east-west across bay)
    "M150,120 Q210,135 270,130 T390,150",
    // I-880 (east bay)
    "M300,40 Q295,140 310,280",
  ],
  districts: [
    { x: 135, y: 65, text: "Marina" },
    { x: 130, y: 105, text: "FiDi", bold: true },
    { x: 145, y: 145, text: "SoMa", bold: true },
    { x: 105, y: 175, text: "Mission" },
    { x: 145, y: 210, text: "Daly City" },
    { x: 115, y: 245, text: "Palo Alto" },
    { x: 280, y: 130, text: "Oakland" },
    { x: 320, y: 195, text: "Fremont" },
    { x: 335, y: 245, text: "San Jose" },
  ],
  pins: [
    { x: 145, y: 140, type: "hq", label: "SoMa HQ" },
    { x: 130, y: 100, type: "client", label: "FiDi" },
    { x: 115, y: 245, type: "client", label: "Palo Alto" },
    { x: 285, y: 130, type: "client", label: "Oakland" },
    { x: 335, y: 240, type: "client" },
    { x: 105, y: 175, type: "client" },
  ],
  stats: [
    { value: "90+", label: "Bay clients" },
    { value: "SaaS", label: "+ AI fluent" },
    { value: "35+", label: "Local engineers" },
    { value: "PT", label: "Coast aligned" },
  ],
  recentClients: [
    "Microsoft",
    "Hyperlogs",
    "Online Rewards",
    "SmileML",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "AI + SaaS native",
      subtitle: "OpenAI · Anthropic · LangChain",
    },
    {
      Icon: MapPin,
      title: "SoMa + Peninsula",
      subtitle: "On-site every sprint",
    },
  ],
};

/* -------- Chicago (Loop + neighborhoods) -------- */
export const chicagoMapConfig = {
  id: "chicago",
  region: "Greater Chicago",
  subtitle: "Loop · River North · Fulton Market",
  searchPlaceholder: "Chicago · Loop + neighborhoods",
  accentColor: "#10B981",
  waterAreas: [
    // Lake Michigan (right side)
    { d: "M280,0 Q260,140 275,280 L400,280 L400,0 Z", opacity: 0.09 },
    // Chicago River (stylized branch)
    { d: "M120,140 Q180,150 230,140 Q235,160 195,170 Q140,165 110,160 Z", opacity: 0.06 },
  ],
  roads: [
    // I-90 / I-94 Kennedy corridor (NW to Loop)
    "M40,40 Q120,100 200,150 T270,180",
    // I-290 Eisenhower (west to Loop)
    "M0,170 Q120,160 220,165 T270,175",
    // I-94 Dan Ryan (south from Loop)
    "M230,160 Q235,210 240,280",
    // Lake Shore Drive
    "M260,40 Q255,140 265,280",
  ],
  districts: [
    { x: 80, y: 55, text: "Evanston" },
    { x: 130, y: 95, text: "Wicker Park" },
    { x: 175, y: 125, text: "River North", bold: true },
    { x: 230, y: 145, text: "The Loop", bold: true },
    { x: 175, y: 165, text: "Fulton Market" },
    { x: 235, y: 200, text: "South Loop" },
    { x: 165, y: 230, text: "Hyde Park" },
    { x: 90, y: 195, text: "Oak Park" },
  ],
  pins: [
    { x: 230, y: 140, type: "hq", label: "The Loop" },
    { x: 175, y: 120, type: "client", label: "River North" },
    { x: 175, y: 165, type: "client", label: "Fulton Market" },
    { x: 165, y: 230, type: "client", label: "Hyde Park" },
    { x: 130, y: 95, type: "client" },
    { x: 235, y: 200, type: "client" },
  ],
  stats: [
    { value: "55+", label: "Chicago clients" },
    { value: "B2B", label: "+ Industrial" },
    { value: "25+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "American Water Works Assoc.",
    "Yes! Communities",
    "FiscalNote",
    "BrokerBlocks",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "B2B + Industrial",
      subtitle: "Insurance · logistics · manufacturing",
    },
    {
      Icon: MapPin,
      title: "Loop + Fulton Market",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Dallas–Fort Worth (Metroplex) -------- */
export const dallasMapConfig = {
  id: "dallas",
  region: "Dallas–Fort Worth",
  subtitle: "Downtown · Uptown · Plano · Fort Worth",
  searchPlaceholder: "DFW · Metroplex",
  accentColor: "#10B981",
  waterAreas: [
    // White Rock Lake (stylized)
    { d: "M250,150 Q280,145 290,170 Q280,190 255,185 Q245,170 250,150 Z", opacity: 0.1 },
  ],
  roads: [
    // I-35 split (E/W) + I-30 corridor
    "M120,0 Q140,140 130,280",
    "M0,150 Q150,140 280,150 T400,160",
    // Dallas North Tollway
    "M210,0 L210,280",
  ],
  districts: [
    { x: 200, y: 55, text: "Frisco" },
    { x: 210, y: 95, text: "Plano" },
    { x: 70, y: 150, text: "Fort Worth", bold: true },
    { x: 165, y: 130, text: "Irving" },
    { x: 225, y: 150, text: "Downtown", bold: true },
    { x: 230, y: 110, text: "Uptown" },
    { x: 250, y: 215, text: "Arlington" },
  ],
  pins: [
    { x: 225, y: 145, type: "hq", label: "Downtown" },
    { x: 230, y: 110, type: "client", label: "Uptown" },
    { x: 210, y: 95, type: "client", label: "Plano" },
    { x: 75, y: 150, type: "client", label: "Fort Worth" },
    { x: 165, y: 130, type: "client" },
    { x: 250, y: 210, type: "client" },
  ],
  stats: [
    { value: "40+", label: "DFW clients" },
    { value: "Fin.", label: "+ Logistics" },
    { value: "20+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "Online Rewards",
    "Yes! Communities",
    "Hyperlogs",
    "BrokerBlocks",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Fintech + Enterprise",
      subtitle: "PCI-aware engineering",
    },
    {
      Icon: MapPin,
      title: "Downtown + Plano",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Atlanta (metro) -------- */
export const atlantaMapConfig = {
  id: "atlanta",
  region: "Metro Atlanta",
  subtitle: "Midtown · Buckhead · Alpharetta · Airport",
  searchPlaceholder: "Atlanta · I-285 Perimeter",
  accentColor: "#EC4899",
  waterAreas: [
    // Chattahoochee River (stylized, NW)
    { d: "M60,40 Q90,110 80,200 Q70,240 95,280 L60,280 Q45,180 40,40 Z", opacity: 0.08 },
  ],
  roads: [
    // I-285 Perimeter
    "M110,70 Q60,160 110,250 Q200,300 290,250 Q350,160 290,70 Q200,30 110,70 Z",
    // I-75 / I-85 Connector (north-south)
    "M200,0 L195,280",
    // I-20 (east-west)
    "M0,180 L400,180",
  ],
  districts: [
    { x: 215, y: 55, text: "Alpharetta" },
    { x: 235, y: 95, text: "Buckhead" },
    { x: 175, y: 130, text: "Midtown", bold: true },
    { x: 195, y: 165, text: "Downtown", bold: true },
    { x: 95, y: 140, text: "Smyrna" },
    { x: 270, y: 150, text: "Decatur" },
    { x: 190, y: 235, text: "Airport" },
  ],
  pins: [
    { x: 178, y: 130, type: "hq", label: "Midtown" },
    { x: 235, y: 95, type: "client", label: "Buckhead" },
    { x: 215, y: 60, type: "client", label: "Alpharetta" },
    { x: 195, y: 165, type: "client", label: "Downtown" },
    { x: 270, y: 150, type: "client" },
    { x: 190, y: 230, type: "client" },
  ],
  stats: [
    { value: "35+", label: "Atlanta clients" },
    { value: "Pmts.", label: "+ Logistics" },
    { value: "18+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "Online Rewards",
    "TruthQuotient",
    "CrowdKeep",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "Payments + Media",
      subtitle: "Transaction Alley fluent",
    },
    {
      Icon: MapPin,
      title: "Midtown + Buckhead",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Phoenix (the Valley) -------- */
export const phoenixMapConfig = {
  id: "phoenix",
  region: "Phoenix & the Valley",
  subtitle: "Downtown · Scottsdale · Tempe · Chandler",
  searchPlaceholder: "Phoenix · Valley of the Sun",
  accentColor: "#F59E0B",
  waterAreas: [
    // Salt River (stylized, east-west low band)
    { d: "M40,210 Q150,200 260,210 Q330,215 380,225 L380,240 Q300,230 200,225 Q120,222 40,228 Z", opacity: 0.07 },
  ],
  roads: [
    // I-10 (curves through metro)
    "M0,120 Q120,150 200,170 Q280,190 400,200",
    // I-17 (north-south)
    "M150,0 Q160,120 150,280",
    // Loop 101 / 202 (stylized ring)
    "M120,90 Q90,170 150,240 Q240,280 310,220 Q350,150 300,90 Q220,55 120,90 Z",
  ],
  districts: [
    { x: 110, y: 70, text: "Glendale" },
    { x: 175, y: 90, text: "Scottsdale" },
    { x: 150, y: 150, text: "Downtown", bold: true },
    { x: 200, y: 150, text: "Tempe", bold: true },
    { x: 245, y: 185, text: "Mesa" },
    { x: 215, y: 215, text: "Chandler" },
    { x: 95, y: 175, text: "Tolleson" },
  ],
  pins: [
    { x: 152, y: 150, type: "hq", label: "Downtown" },
    { x: 180, y: 90, type: "client", label: "Scottsdale" },
    { x: 200, y: 150, type: "client", label: "Tempe" },
    { x: 245, y: 185, type: "client", label: "Mesa" },
    { x: 215, y: 215, type: "client" },
    { x: 110, y: 72, type: "client" },
  ],
  stats: [
    { value: "30+", label: "Valley clients" },
    { value: "Fin.", label: "+ Healthcare" },
    { value: "15+", label: "Local engineers" },
    { value: "MT", label: "Time aligned" },
  ],
  recentClients: [
    "Open Tech Alliance",
    "Online Rewards",
    "SmileML",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Fintech + Healthcare",
      subtitle: "Compliance fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Tempe",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Houston (Gulf Coast metro) -------- */
export const houstonMapConfig = {
  id: "houston",
  region: "Greater Houston",
  subtitle: "Downtown · Med Center · Energy Corridor · Port",
  searchPlaceholder: "Houston · Gulf Coast metro",
  accentColor: "#F59E0B",
  waterAreas: [
    // Buffalo Bayou (stylized, east-west)
    { d: "M40,160 Q150,150 250,165 Q320,175 380,190 L380,205 Q300,195 220,185 Q130,178 40,185 Z", opacity: 0.07 },
    // Galveston Bay hint (SE corner)
    { d: "M320,220 Q365,225 385,275 L300,275 Z", opacity: 0.09 },
  ],
  roads: [
    // I-610 Loop
    "M130,80 Q80,160 130,235 Q210,285 290,235 Q340,160 290,80 Q210,45 130,80 Z",
    // I-10 (east-west)
    "M0,120 L400,130",
    // I-45 (NW to SE)
    "M120,0 Q180,140 270,280",
    // US-59 / I-69
    "M40,60 Q160,150 320,250",
  ],
  districts: [
    { x: 120, y: 70, text: "The Woodlands" },
    { x: 90, y: 140, text: "Energy Corridor" },
    { x: 205, y: 130, text: "Downtown", bold: true },
    { x: 235, y: 95, text: "Heights" },
    { x: 200, y: 185, text: "Med Center", bold: true },
    { x: 295, y: 215, text: "Port / Pasadena" },
    { x: 150, y: 215, text: "Sugar Land" },
  ],
  pins: [
    { x: 205, y: 128, type: "hq", label: "Downtown" },
    { x: 200, y: 185, type: "client", label: "Med Center" },
    { x: 92, y: 140, type: "client", label: "Energy Corridor" },
    { x: 295, y: 210, type: "client", label: "Port" },
    { x: 120, y: 72, type: "client" },
    { x: 150, y: 213, type: "client" },
  ],
  stats: [
    { value: "30+", label: "Houston clients" },
    { value: "Energy", label: "+ Healthcare" },
    { value: "15+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "American Water Works Assoc.",
    "SCS Engineers",
    "Online Rewards",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Energy + Medical",
      subtitle: "SCADA & HIPAA fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Med Center",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- San Diego (SoCal metro) -------- */
export const sanDiegoMapConfig = {
  id: "san-diego",
  region: "Greater San Diego",
  subtitle: "Downtown · La Jolla · Sorrento Valley · Coronado",
  searchPlaceholder: "San Diego · SoCal coast",
  accentColor: "#00AEEF",
  waterAreas: [
    // Pacific Ocean (left band)
    { d: "M0,0 L70,0 Q55,140 65,280 L0,280 Z", opacity: 0.08 },
    // San Diego Bay
    { d: "M150,210 Q180,240 175,280 L120,280 Q130,235 150,210 Z", opacity: 0.09 },
  ],
  roads: [
    // I-5 (coastal, north-south)
    "M110,0 Q125,140 140,280",
    // I-15 (inland)
    "M250,0 Q245,140 255,280",
    // I-8 (east-west)
    "M60,150 L400,160",
    // CA-52
    "M90,110 Q200,105 320,120",
  ],
  districts: [
    { x: 95, y: 70, text: "Carlsbad" },
    { x: 90, y: 115, text: "La Jolla" },
    { x: 175, y: 110, text: "Sorrento Valley" },
    { x: 260, y: 95, text: "Escondido" },
    { x: 150, y: 180, text: "Downtown", bold: true },
    { x: 235, y: 175, text: "Mission Valley", bold: true },
    { x: 120, y: 230, text: "Coronado" },
    { x: 285, y: 210, text: "Chula Vista" },
  ],
  pins: [
    { x: 150, y: 178, type: "hq", label: "Downtown" },
    { x: 175, y: 110, type: "client", label: "Sorrento Valley" },
    { x: 92, y: 115, type: "client", label: "La Jolla" },
    { x: 235, y: 172, type: "client", label: "Mission Valley" },
    { x: 95, y: 72, type: "client" },
    { x: 285, y: 205, type: "client" },
  ],
  stats: [
    { value: "30+", label: "SD clients" },
    { value: "Bio.", label: "+ Defense" },
    { value: "15+", label: "Local engineers" },
    { value: "PT", label: "Coast aligned" },
  ],
  recentClients: [
    "SmileML",
    "Open Tech Alliance",
    "Online Rewards",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Biotech + Health",
      subtitle: "HIPAA · HITRUST fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Sorrento Valley",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Philadelphia (metro) -------- */
export const philadelphiaMapConfig = {
  id: "philadelphia",
  region: "Greater Philadelphia",
  subtitle: "Center City · University City · King of Prussia",
  searchPlaceholder: "Philadelphia · Delaware Valley",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Delaware River (east)
    { d: "M270,0 Q255,140 275,280 L320,280 Q300,140 315,0 Z", opacity: 0.09 },
    // Schuylkill River (stylized diagonal)
    { d: "M120,40 Q160,140 210,230 Q222,245 205,250 Q160,160 110,60 Z", opacity: 0.06 },
  ],
  roads: [
    // I-95 (along Delaware)
    "M240,0 Q235,140 250,280",
    // I-76 Schuylkill Expwy
    "M90,60 Q160,150 230,235",
    // I-476 (Blue Route)
    "M120,0 Q110,140 130,280",
    // Market St (east-west)
    "M60,150 L300,150",
  ],
  districts: [
    { x: 95, y: 70, text: "King of Prussia" },
    { x: 150, y: 110, text: "Manayunk" },
    { x: 175, y: 165, text: "University City" },
    { x: 215, y: 150, text: "Center City", bold: true },
    { x: 235, y: 105, text: "Fishtown" },
    { x: 130, y: 210, text: "Main Line" },
    { x: 245, y: 210, text: "Navy Yard", bold: true },
    { x: 300, y: 130, text: "Camden" },
  ],
  pins: [
    { x: 213, y: 150, type: "hq", label: "Center City" },
    { x: 175, y: 163, type: "client", label: "University City" },
    { x: 95, y: 72, type: "client", label: "King of Prussia" },
    { x: 245, y: 205, type: "client", label: "Navy Yard" },
    { x: 235, y: 105, type: "client" },
    { x: 130, y: 210, type: "client" },
  ],
  stats: [
    { value: "35+", label: "Philly clients" },
    { value: "Health", label: "+ Higher-Ed" },
    { value: "18+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "PAN Foundation",
    "FiscalNote",
    "SmileML",
    "Toastmasters International",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Health + Pharma",
      subtitle: "HIPAA · HITRUST fluent",
    },
    {
      Icon: MapPin,
      title: "Center City + Univ. City",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Nashville (Middle Tennessee) -------- */
export const nashvilleMapConfig = {
  id: "nashville",
  region: "Greater Nashville",
  subtitle: "Downtown · The Gulch · Franklin · Brentwood",
  searchPlaceholder: "Nashville · Middle Tennessee",
  accentColor: "#F59E0B",
  waterAreas: [
    // Cumberland River (winding through)
    { d: "M150,40 Q200,110 190,170 Q180,220 220,270 Q235,275 215,255 Q180,200 205,150 Q220,110 175,50 Z", opacity: 0.08 },
  ],
  roads: [
    // I-40 (east-west)
    "M0,150 Q150,140 300,150 T400,155",
    // I-65 (north-south)
    "M180,0 Q190,140 200,280",
    // I-24
    "M60,40 Q160,150 300,250",
    // I-440 loop hint
    "M120,110 Q200,100 280,120",
  ],
  districts: [
    { x: 100, y: 70, text: "Germantown" },
    { x: 235, y: 95, text: "East Nashville" },
    { x: 175, y: 140, text: "Downtown", bold: true },
    { x: 150, y: 175, text: "The Gulch", bold: true },
    { x: 250, y: 165, text: "Music Row" },
    { x: 95, y: 200, text: "Bellevue" },
    { x: 185, y: 235, text: "Brentwood" },
    { x: 260, y: 230, text: "Franklin" },
  ],
  pins: [
    { x: 173, y: 140, type: "hq", label: "Downtown" },
    { x: 150, y: 173, type: "client", label: "The Gulch" },
    { x: 250, y: 162, type: "client", label: "Music Row" },
    { x: 235, y: 95, type: "client", label: "East Nashville" },
    { x: 260, y: 225, type: "client" },
    { x: 100, y: 72, type: "client" },
  ],
  stats: [
    { value: "25+", label: "Nashville clients" },
    { value: "Health", label: "+ Music" },
    { value: "12+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "PAN Foundation",
    "Online Rewards",
    "CrowdKeep",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Health Services",
      subtitle: "HIPAA · RCM fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Music Row",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Portland (OR metro) -------- */
export const portlandMapConfig = {
  id: "portland",
  region: "Greater Portland",
  subtitle: "Downtown · Pearl District · Beaverton · Eastside",
  searchPlaceholder: "Portland · Pacific Northwest",
  accentColor: "#10B981",
  waterAreas: [
    // Willamette River (north-south through center)
    { d: "M185,0 Q200,80 195,150 Q190,210 210,280 L175,280 Q160,200 175,120 Q180,50 170,0 Z", opacity: 0.09 },
    // Columbia River hint (north edge)
    { d: "M0,20 Q120,5 220,15 Q320,25 400,10 L400,35 Q280,45 180,35 Q80,25 0,40 Z", opacity: 0.06 },
  ],
  roads: [
    // I-5 (north-south)
    "M200,0 Q205,140 210,280",
    // I-84 (east-west from downtown)
    "M200,120 L400,110",
    // US-26 / Sunset Hwy west
    "M0,140 Q100,135 200,145",
    // I-405 loop hint
    "M150,90 Q140,150 160,210 Q200,230 240,200",
  ],
  districts: [
    { x: 95, y: 80, text: "Beaverton" },
    { x: 130, y: 130, text: "Pearl District", bold: true },
    { x: 175, y: 155, text: "Downtown", bold: true },
    { x: 250, y: 100, text: "Lloyd District" },
    { x: 280, y: 145, text: "Eastside" },
    { x: 110, y: 200, text: "South Waterfront" },
    { x: 240, y: 210, text: "Sellwood" },
    { x: 310, y: 70, text: "Gresham" },
  ],
  pins: [
    { x: 175, y: 153, type: "hq", label: "Downtown" },
    { x: 130, y: 128, type: "client", label: "Pearl District" },
    { x: 95, y: 82, type: "client", label: "Beaverton" },
    { x: 250, y: 100, type: "client", label: "Lloyd District" },
    { x: 110, y: 198, type: "client" },
    { x: 280, y: 145, type: "client" },
  ],
  stats: [
    { value: "25+", label: "Portland clients" },
    { value: "Clean", label: "+ Ecommerce" },
    { value: "12+", label: "Local engineers" },
    { value: "PT", label: "Time aligned" },
  ],
  recentClients: [
    "Online Rewards",
    "Open Tech Alliance",
    "Yes! Communities",
    "CrowdKeep",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "Cleantech + Brands",
      subtitle: "Sustainability fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Pearl",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Minneapolis (Twin Cities) -------- */
export const minneapolisMapConfig = {
  id: "minneapolis",
  region: "Twin Cities Metro",
  subtitle: "Downtown · Uptown · St. Paul · Bloomington",
  searchPlaceholder: "Minneapolis · Twin Cities",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Mississippi River (winding between Minneapolis and St. Paul)
    { d: "M160,0 Q200,70 190,130 Q175,190 230,250 Q245,260 220,245 Q170,190 200,120 Q210,70 175,20 Z", opacity: 0.09 },
    // Chain of Lakes hint (SW)
    { d: "M80,180 Q95,175 100,190 Q95,205 80,200 Q70,190 80,180 Z", opacity: 0.07 },
  ],
  roads: [
    // I-94 (east-west)
    "M0,145 L400,150",
    // I-35W (north-south through Minneapolis)
    "M170,0 Q175,140 180,280",
    // I-35E (through St. Paul)
    "M260,0 Q270,140 280,280",
    // I-494 loop south
    "M60,230 Q200,250 340,235",
  ],
  districts: [
    { x: 100, y: 90, text: "North Loop" },
    { x: 145, y: 140, text: "Downtown Mpls", bold: true },
    { x: 110, y: 185, text: "Uptown" },
    { x: 255, y: 155, text: "St. Paul", bold: true },
    { x: 200, y: 100, text: "University" },
    { x: 160, y: 230, text: "Bloomington" },
    { x: 300, y: 210, text: "Woodbury" },
    { x: 70, y: 140, text: "Edina" },
  ],
  pins: [
    { x: 145, y: 138, type: "hq", label: "Downtown Mpls" },
    { x: 255, y: 153, type: "client", label: "St. Paul" },
    { x: 110, y: 183, type: "client", label: "Uptown" },
    { x: 160, y: 228, type: "client", label: "Bloomington" },
    { x: 100, y: 92, type: "client" },
    { x: 200, y: 100, type: "client" },
  ],
  stats: [
    { value: "30+", label: "MSP clients" },
    { value: "Health", label: "+ Retail" },
    { value: "14+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "PAN Foundation",
    "SmileML",
    "American Water Works Assoc.",
    "FiscalNote",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Health + Retail",
      subtitle: "HIPAA · Commerce fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + St. Paul",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Charlotte (NC metro) -------- */
export const charlotteMapConfig = {
  id: "charlotte",
  region: "Greater Charlotte",
  subtitle: "Uptown · South End · Ballantyne · Concord",
  searchPlaceholder: "Charlotte · Carolinas",
  accentColor: "#EC4899",
  waterAreas: [
    // Catawba River / Lake Norman hint (NW)
    { d: "M40,40 Q80,30 100,70 Q90,110 55,100 Q35,80 40,40 Z", opacity: 0.08 },
  ],
  roads: [
    // I-77 (north-south)
    "M190,0 Q200,140 205,280",
    // I-85 (NE to SW)
    "M320,40 Q220,140 100,240",
    // I-485 outer loop hint
    "M100,60 Q60,150 110,240 Q200,280 290,240 Q340,150 290,60 Q200,30 100,60 Z",
    // Independence Blvd
    "M200,150 L380,130",
  ],
  districts: [
    { x: 175, y: 130, text: "Uptown", bold: true },
    { x: 160, y: 170, text: "South End", bold: true },
    { x: 220, y: 100, text: "NoDa" },
    { x: 120, y: 100, text: "Dilworth" },
    { x: 250, y: 180, text: "Myers Park" },
    { x: 200, y: 230, text: "Ballantyne" },
    { x: 290, y: 90, text: "Concord" },
    { x: 90, y: 200, text: "Airport" },
  ],
  pins: [
    { x: 175, y: 128, type: "hq", label: "Uptown" },
    { x: 160, y: 168, type: "client", label: "South End" },
    { x: 200, y: 228, type: "client", label: "Ballantyne" },
    { x: 290, y: 90, type: "client", label: "Concord" },
    { x: 220, y: 100, type: "client" },
    { x: 120, y: 100, type: "client" },
  ],
  stats: [
    { value: "28+", label: "CLT clients" },
    { value: "Banking", label: "+ Energy" },
    { value: "14+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "Online Rewards",
    "PAN Foundation",
    "SCS Engineers",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Banking + Fintech",
      subtitle: "PCI · SOC 2 fluent",
    },
    {
      Icon: MapPin,
      title: "Uptown + South End",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Salt Lake City (Wasatch Front / Silicon Slopes) -------- */
export const saltLakeCityMapConfig = {
  id: "salt-lake-city",
  region: "Salt Lake City & Silicon Slopes",
  subtitle: "Downtown · Sugar House · Lehi · Park City",
  searchPlaceholder: "SLC · Wasatch Front corridor",
  accentColor: "#00AEEF",
  waterAreas: [
    // Great Salt Lake (NW corner)
    { d: "M0,0 L130,0 Q110,50 70,70 Q30,85 0,75 Z", opacity: 0.09 },
    // Utah Lake hint (SW, near Provo)
    { d: "M60,225 Q90,215 105,240 Q95,265 65,260 Q50,245 60,225 Z", opacity: 0.08 },
  ],
  roads: [
    // I-15 (north-south spine of the Wasatch Front)
    "M170,0 Q180,140 165,280",
    // I-80 (east-west through SLC toward Park City)
    "M0,110 Q150,115 280,95 T400,70",
    // I-215 belt loop hint
    "M130,70 Q100,130 140,180 Q200,210 250,170 Q270,115 230,75 Q180,50 130,70 Z",
  ],
  districts: [
    { x: 165, y: 90, text: "Downtown", bold: true },
    { x: 220, y: 130, text: "Sugar House" },
    { x: 320, y: 70, text: "Park City" },
    { x: 120, y: 140, text: "West Valley" },
    { x: 200, y: 175, text: "Sandy" },
    { x: 175, y: 215, text: "Lehi", bold: true },
    { x: 110, y: 250, text: "Provo" },
  ],
  pins: [
    { x: 168, y: 88, type: "hq", label: "Downtown" },
    { x: 220, y: 127, type: "client", label: "Sugar House" },
    { x: 178, y: 212, type: "client", label: "Lehi" },
    { x: 320, y: 68, type: "client", label: "Park City" },
    { x: 112, y: 247, type: "client" },
    { x: 200, y: 172, type: "client" },
  ],
  stats: [
    { value: "25+", label: "Utah clients" },
    { value: "SaaS", label: "+ Fintech" },
    { value: "12+", label: "Local engineers" },
    { value: "MT", label: "Time aligned" },
  ],
  recentClients: [
    "Open Tech Alliance",
    "Hyperlogs",
    "Online Rewards",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "SaaS + Fintech",
      subtitle: "Silicon Slopes fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Lehi",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Raleigh (Research Triangle) -------- */
export const raleighMapConfig = {
  id: "raleigh",
  region: "Raleigh & the Research Triangle",
  subtitle: "Downtown · North Hills · RTP · Durham",
  searchPlaceholder: "Raleigh · Research Triangle",
  accentColor: "#10B981",
  waterAreas: [
    // Falls Lake / Neuse River hint (NE)
    { d: "M300,30 Q340,25 365,55 Q355,90 320,80 Q295,60 300,30 Z", opacity: 0.08 },
    // Jordan Lake hint (SW)
    { d: "M75,190 Q100,180 110,210 Q100,240 70,230 Q60,210 75,190 Z", opacity: 0.08 },
  ],
  roads: [
    // I-40 (east-west through the Triangle)
    "M0,150 Q120,165 240,155 T400,140",
    // I-540 outer loop hint around Raleigh
    "M230,60 Q300,90 310,160 Q290,230 220,240 Q160,220 170,150 Q180,90 230,60 Z",
    // US-15/501 Durham–Chapel Hill corridor
    "M90,50 Q80,120 60,200",
  ],
  districts: [
    { x: 120, y: 65, text: "Durham", bold: true },
    { x: 60, y: 120, text: "Chapel Hill" },
    { x: 145, y: 125, text: "RTP", bold: true },
    { x: 265, y: 100, text: "North Hills" },
    { x: 255, y: 160, text: "Downtown" },
    { x: 170, y: 195, text: "Cary" },
    { x: 300, y: 220, text: "Garner" },
  ],
  pins: [
    { x: 257, y: 157, type: "hq", label: "Downtown" },
    { x: 148, y: 122, type: "client", label: "RTP" },
    { x: 122, y: 62, type: "client", label: "Durham" },
    { x: 265, y: 97, type: "client", label: "North Hills" },
    { x: 62, y: 117, type: "client" },
    { x: 172, y: 192, type: "client" },
  ],
  stats: [
    { value: "22+", label: "Triangle clients" },
    { value: "Bio.", label: "+ EdTech" },
    { value: "12+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "FiscalNote",
    "SCS Engineers",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: Brain,
      title: "Biotech + EdTech",
      subtitle: "HIPAA · GxP aware",
    },
    {
      Icon: MapPin,
      title: "Downtown + RTP",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Tampa (Tampa Bay metro) -------- */
export const tampaMapConfig = {
  id: "tampa",
  region: "Tampa Bay",
  subtitle: "Downtown · Ybor City · Westshore · St. Pete",
  searchPlaceholder: "Tampa · Bay area metro",
  accentColor: "#F59E0B",
  waterAreas: [
    // Tampa Bay (central body cutting up from the south)
    { d: "M120,280 Q135,200 175,170 Q215,150 235,190 Q245,240 230,280 Z", opacity: 0.1 },
    // Gulf of Mexico (left edge)
    { d: "M0,0 L55,0 Q40,140 50,280 L0,280 Z", opacity: 0.08 },
  ],
  roads: [
    // I-275 (through St. Pete, across the bay, up through Tampa)
    "M90,260 Q110,190 180,140 Q240,105 260,40",
    // I-4 (northeast toward Orlando)
    "M245,120 Q310,95 400,80",
    // I-75 (north-south, east side)
    "M330,0 Q325,140 335,280",
  ],
  districts: [
    { x: 250, y: 115, text: "Downtown", bold: true },
    { x: 300, y: 130, text: "Ybor City" },
    { x: 185, y: 110, text: "Westshore" },
    { x: 95, y: 220, text: "St. Petersburg", bold: true },
    { x: 340, y: 190, text: "Brandon" },
    { x: 235, y: 225, text: "MacDill" },
    { x: 265, y: 55, text: "USF / North Tampa" },
  ],
  pins: [
    { x: 252, y: 112, type: "hq", label: "Downtown" },
    { x: 188, y: 107, type: "client", label: "Westshore" },
    { x: 98, y: 217, type: "client", label: "St. Pete" },
    { x: 300, y: 127, type: "client", label: "Ybor City" },
    { x: 340, y: 187, type: "client" },
    { x: 265, y: 58, type: "client" },
  ],
  stats: [
    { value: "24+", label: "Bay clients" },
    { value: "Fin.", label: "+ Defense" },
    { value: "12+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Online Rewards",
    "BrokerBlocks",
    "CrowdKeep",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Fintech + Defense",
      subtitle: "PCI · security fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Westshore",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Detroit (Metro Detroit) -------- */
export const detroitMapConfig = {
  id: "detroit",
  region: "Metro Detroit",
  subtitle: "Downtown · Midtown · Corktown · Dearborn",
  searchPlaceholder: "Detroit · Metro Detroit",
  accentColor: "#EC4899",
  waterAreas: [
    // Detroit River / Lake St. Clair edge (southeast)
    { d: "M280,80 Q320,120 340,180 Q350,230 330,280 L400,280 L400,60 Z", opacity: 0.1 },
  ],
  roads: [
    // I-75 (north-south through downtown)
    "M210,0 Q205,140 220,280",
    // I-94 (west-east toward Dearborn / airport)
    "M0,170 Q140,155 230,160 T400,150",
    // M-10 Lodge / I-96 corridor
    "M40,40 Q120,100 200,155",
  ],
  districts: [
    { x: 220, y: 145, text: "Downtown", bold: true },
    { x: 200, y: 115, text: "Midtown", bold: true },
    { x: 175, y: 160, text: "Corktown" },
    { x: 110, y: 185, text: "Dearborn" },
    { x: 70, y: 230, text: "Ann Arbor" },
    { x: 280, y: 55, text: "Troy" },
  ],
  pins: [
    { x: 222, y: 142, type: "hq", label: "Downtown" },
    { x: 200, y: 112, type: "client", label: "Midtown" },
    { x: 175, y: 157, type: "client", label: "Corktown" },
    { x: 110, y: 182, type: "client", label: "Dearborn" },
    { x: 70, y: 227, type: "client" },
    { x: 280, y: 55, type: "client" },
  ],
  stats: [
    { value: "28+", label: "Metro clients" },
    { value: "Auto", label: "+ Manufacturing" },
    { value: "14+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Hyperlogs",
    "SCS Engineers",
    "BrokerBlocks",
    "Online Rewards",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "Auto + Manufacturing",
      subtitle: "Mobility · plant-ops fluent",
    },
    {
      Icon: MapPin,
      title: "Downtown + Midtown",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Orlando (Central Florida) -------- */
export const orlandoMapConfig = {
  id: "orlando",
  region: "Central Florida",
  subtitle: "Downtown · Lake Nona · Winter Park · I-Drive",
  searchPlaceholder: "Orlando · Central Florida",
  accentColor: "#00AEEF",
  waterAreas: [
    // Lake Eola / chain of lakes (stylized center)
    { d: "M190,130 Q220,120 235,145 Q225,170 195,165 Q180,150 190,130 Z", opacity: 0.1 },
  ],
  roads: [
    // I-4 (southwest to northeast)
    "M40,240 Q160,160 280,90 Q340,55 400,40",
    // Florida Turnpike (east side)
    "M310,0 Q300,140 320,280",
    // Beachline / 528 corridor
    "M220,160 Q300,175 400,170",
  ],
  districts: [
    { x: 210, y: 140, text: "Downtown", bold: true },
    { x: 290, y: 195, text: "Lake Nona", bold: true },
    { x: 230, y: 95, text: "Winter Park" },
    { x: 145, y: 185, text: "International Drive" },
    { x: 120, y: 230, text: "Kissimmee" },
    { x: 95, y: 200, text: "Celebration" },
  ],
  pins: [
    { x: 212, y: 137, type: "hq", label: "Downtown" },
    { x: 290, y: 192, type: "client", label: "Lake Nona" },
    { x: 230, y: 92, type: "client", label: "Winter Park" },
    { x: 145, y: 182, type: "client", label: "I-Drive" },
    { x: 120, y: 227, type: "client" },
    { x: 95, y: 197, type: "client" },
  ],
  stats: [
    { value: "22+", label: "Orlando clients" },
    { value: "Tour.", label: "+ Healthcare" },
    { value: "11+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Yes! Communities",
    "SmileML",
    "CrowdKeep",
    "Toastmasters International",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Tourism + Healthcare",
      subtitle: "Guest UX · HIPAA aware",
    },
    {
      Icon: Sparkles,
      title: "Downtown + Lake Nona",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Columbus (Central Ohio) -------- */
export const columbusMapConfig = {
  id: "columbus",
  region: "Central Ohio",
  subtitle: "Downtown · Short North · Dublin · Easton",
  searchPlaceholder: "Columbus · Central Ohio",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Scioto River (stylized north-south through downtown)
    { d: "M175,40 Q185,120 180,200 Q175,250 190,280 L155,280 Q150,200 160,120 Q165,60 175,40 Z", opacity: 0.08 },
  ],
  roads: [
    // I-71 (north-south)
    "M220,0 Q215,140 230,280",
    // I-70 (west-east)
    "M0,165 Q140,155 220,160 T400,155",
    // I-270 outer belt (partial arc)
    "M60,60 Q40,140 70,230 Q160,270 260,240 Q340,180 320,80",
  ],
  districts: [
    { x: 210, y: 155, text: "Downtown", bold: true },
    { x: 215, y: 120, text: "Short North", bold: true },
    { x: 100, y: 90, text: "Dublin" },
    { x: 300, y: 100, text: "New Albany" },
    { x: 280, y: 140, text: "Easton" },
    { x: 200, y: 195, text: "German Village" },
  ],
  pins: [
    { x: 212, y: 152, type: "hq", label: "Downtown" },
    { x: 215, y: 117, type: "client", label: "Short North" },
    { x: 100, y: 87, type: "client", label: "Dublin" },
    { x: 280, y: 137, type: "client", label: "Easton" },
    { x: 300, y: 97, type: "client" },
    { x: 200, y: 192, type: "client" },
  ],
  stats: [
    { value: "26+", label: "Columbus clients" },
    { value: "Ins.", label: "+ Retail HQ" },
    { value: "13+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "Online Rewards",
    "American Water Works Assoc.",
    "PAN Foundation",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Insurance + Retail",
      subtitle: "Enterprise delivery",
    },
    {
      Icon: Brain,
      title: "Downtown + Short North",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Indianapolis (Central Indiana) -------- */
export const indianapolisMapConfig = {
  id: "indianapolis",
  region: "Central Indiana",
  subtitle: "Downtown · Broad Ripple · Carmel · Fishers",
  searchPlaceholder: "Indianapolis · Central Indiana",
  accentColor: "#10B981",
  waterAreas: [
    // White River (stylized north-south west of downtown)
    { d: "M155,30 Q165,120 160,200 Q155,250 170,280 L140,280 Q135,200 145,120 Q150,50 155,30 Z", opacity: 0.08 },
  ],
  roads: [
    // I-65 (northwest-southeast)
    "M80,0 Q160,120 280,280",
    // I-70 (west-east)
    "M0,160 Q140,150 220,155 T400,150",
    // I-465 outer belt (partial)
    "M70,50 Q40,140 80,230 Q180,280 300,240 Q360,160 320,60",
  ],
  districts: [
    { x: 210, y: 155, text: "Downtown", bold: true },
    { x: 220, y: 95, text: "Broad Ripple", bold: true },
    { x: 250, y: 55, text: "Carmel" },
    { x: 310, y: 80, text: "Fishers" },
    { x: 230, y: 140, text: "Mass Ave" },
    { x: 200, y: 195, text: "Fountain Square" },
  ],
  pins: [
    { x: 212, y: 152, type: "hq", label: "Downtown" },
    { x: 220, y: 92, type: "client", label: "Broad Ripple" },
    { x: 250, y: 52, type: "client", label: "Carmel" },
    { x: 310, y: 77, type: "client", label: "Fishers" },
    { x: 230, y: 137, type: "client" },
    { x: 200, y: 192, type: "client" },
  ],
  stats: [
    { value: "24+", label: "Indy clients" },
    { value: "Log.", label: "+ Life sci." },
    { value: "12+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Online Rewards",
    "Hyperlogs",
    "Yes! Communities",
    "CrowdKeep",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Logistics + Life Sci",
      subtitle: "Distribution · HIPAA aware",
    },
    {
      Icon: MapPin,
      title: "Downtown + Carmel",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Pittsburgh (Western Pennsylvania) -------- */
export const pittsburghMapConfig = {
  id: "pittsburgh",
  region: "Western Pennsylvania",
  subtitle: "Downtown · Oakland · Strip District · South Side",
  searchPlaceholder: "Pittsburgh · Western PA",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Three Rivers confluence (stylized)
    { d: "M120,150 Q180,140 210,160 Q240,145 280,155 L270,190 Q220,175 180,185 Q140,175 120,150 Z", opacity: 0.1 },
  ],
  roads: [
    // I-376 (west-east)
    "M0,170 Q140,160 220,165 T400,160",
    // I-279 (north-south)
    "M200,0 Q195,140 210,280",
    // Parkway East / 376 spur
    "M220,165 Q300,180 400,200",
  ],
  districts: [
    { x: 195, y: 155, text: "Downtown", bold: true },
    { x: 260, y: 145, text: "Oakland", bold: true },
    { x: 230, y: 120, text: "Strip District" },
    { x: 210, y: 200, text: "South Side" },
    { x: 290, y: 120, text: "Shadyside" },
    { x: 270, y: 95, text: "Lawrenceville" },
  ],
  pins: [
    { x: 197, y: 152, type: "hq", label: "Downtown" },
    { x: 260, y: 142, type: "client", label: "Oakland" },
    { x: 230, y: 117, type: "client", label: "Strip District" },
    { x: 210, y: 197, type: "client", label: "South Side" },
    { x: 290, y: 117, type: "client" },
    { x: 270, y: 92, type: "client" },
  ],
  stats: [
    { value: "21+", label: "Pgh clients" },
    { value: "Rob.", label: "+ Healthcare" },
    { value: "11+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "FiscalNote",
    "TruthQuotient",
    "Open Tech Alliance",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "Robotics + Healthcare",
      subtitle: "Hard tech · HIPAA aware",
    },
    {
      Icon: Brain,
      title: "Downtown + Oakland",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Kansas City (MO / KS metro) -------- */
export const kansasCityMapConfig = {
  id: "kansas-city",
  region: "Kansas City Metro",
  subtitle: "Downtown · Crossroads · Plaza · Overland Park",
  searchPlaceholder: "Kansas City · MO/KS metro",
  accentColor: "#F59E0B",
  waterAreas: [
    // Missouri River (stylized north edge)
    { d: "M40,70 Q140,55 220,75 Q300,95 380,80 L380,110 Q290,120 210,100 Q120,85 40,100 Z", opacity: 0.08 },
  ],
  roads: [
    // I-70 (west-east)
    "M0,145 Q140,135 220,140 T400,135",
    // I-35 (southwest-northeast)
    "M60,280 Q160,180 260,80 Q320,40 400,20",
    // I-435 outer belt (partial)
    "M80,40 Q40,140 90,240 Q200,290 320,250 Q380,170 340,60",
  ],
  districts: [
    { x: 200, y: 145, text: "Downtown", bold: true },
    { x: 190, y: 175, text: "Crossroads", bold: true },
    { x: 230, y: 200, text: "Plaza" },
    { x: 300, y: 210, text: "Overland Park" },
    { x: 215, y: 165, text: "Midtown" },
    { x: 175, y: 190, text: "Westport" },
  ],
  pins: [
    { x: 202, y: 142, type: "hq", label: "Downtown" },
    { x: 190, y: 172, type: "client", label: "Crossroads" },
    { x: 230, y: 197, type: "client", label: "Plaza" },
    { x: 300, y: 207, type: "client", label: "Overland Park" },
    { x: 215, y: 162, type: "client" },
    { x: 175, y: 187, type: "client" },
  ],
  stats: [
    { value: "20+", label: "KC clients" },
    { value: "Log.", label: "+ Animal health" },
    { value: "10+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "BrokerBlocks",
    "SCS Engineers",
    "American Water Works Assoc.",
    "Hyperlogs",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Logistics + Animal Health",
      subtitle: "Field-ready delivery",
    },
    {
      Icon: Sparkles,
      title: "Downtown + Crossroads",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Cleveland (Northeast Ohio) -------- */
export const clevelandMapConfig = {
  id: "cleveland",
  region: "Northeast Ohio",
  subtitle: "Downtown · University Circle · Ohio City · Beachwood",
  searchPlaceholder: "Cleveland · Northeast Ohio",
  accentColor: "#00AEEF",
  waterAreas: [
    // Lake Erie (stylized north edge)
    { d: "M0,40 Q120,20 220,35 Q320,50 400,30 L400,70 Q300,85 200,70 Q100,55 0,75 Z", opacity: 0.1 },
  ],
  roads: [
    // I-90 (west-east along lake)
    "M0,95 Q140,85 220,90 T400,85",
    // I-77 (north-south)
    "M210,90 Q205,160 220,280",
    // I-480 (southern arc)
    "M40,200 Q140,240 240,220 Q340,200 400,210",
  ],
  districts: [
    { x: 200, y: 130, text: "Downtown", bold: true },
    { x: 260, y: 120, text: "University Circle", bold: true },
    { x: 160, y: 145, text: "Ohio City" },
    { x: 175, y: 175, text: "Tremont" },
    { x: 310, y: 140, text: "Beachwood" },
    { x: 120, y: 120, text: "Lakewood" },
  ],
  pins: [
    { x: 202, y: 127, type: "hq", label: "Downtown" },
    { x: 260, y: 117, type: "client", label: "University Circle" },
    { x: 160, y: 142, type: "client", label: "Ohio City" },
    { x: 310, y: 137, type: "client", label: "Beachwood" },
    { x: 120, y: 117, type: "client" },
    { x: 175, y: 172, type: "client" },
  ],
  stats: [
    { value: "22+", label: "Cleveland clients" },
    { value: "Health", label: "+ Manufacturing" },
    { value: "11+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "Online Rewards",
    "FiscalNote",
    "American Water Works Assoc.",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Healthcare + Manufacturing",
      subtitle: "HIPAA aware · Industrial ops",
    },
    {
      Icon: MapPin,
      title: "Downtown + University Circle",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Milwaukee (Southeast Wisconsin) -------- */
export const milwaukeeMapConfig = {
  id: "milwaukee",
  region: "Southeast Wisconsin",
  subtitle: "Downtown · Third Ward · Bay View · Wauwatosa",
  searchPlaceholder: "Milwaukee · SE Wisconsin",
  accentColor: "#10B981",
  waterAreas: [
    // Lake Michigan (stylized east edge)
    { d: "M340,0 Q370,80 360,160 Q350,220 380,280 L400,280 L400,0 Z", opacity: 0.1 },
  ],
  roads: [
    // I-94 (west-east / downtown)
    "M0,150 Q140,140 220,145 T360,140",
    // I-43 (north-south)
    "M230,0 Q225,140 240,280",
    // I-894 southern belt (partial)
    "M60,220 Q160,250 280,230 Q340,210 380,200",
  ],
  districts: [
    { x: 210, y: 145, text: "Downtown", bold: true },
    { x: 230, y: 170, text: "Third Ward", bold: true },
    { x: 240, y: 210, text: "Bay View" },
    { x: 120, y: 140, text: "Wauwatosa" },
    { x: 90, y: 120, text: "Brookfield" },
    { x: 250, y: 110, text: "East Side" },
  ],
  pins: [
    { x: 212, y: 142, type: "hq", label: "Downtown" },
    { x: 230, y: 167, type: "client", label: "Third Ward" },
    { x: 240, y: 207, type: "client", label: "Bay View" },
    { x: 120, y: 137, type: "client", label: "Wauwatosa" },
    { x: 90, y: 117, type: "client" },
    { x: 250, y: 107, type: "client" },
  ],
  stats: [
    { value: "20+", label: "MKE clients" },
    { value: "Mfg.", label: "+ Healthcare" },
    { value: "10+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "Hyperlogs",
    "Yes! Communities",
    "SCS Engineers",
    "CrowdKeep",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "Manufacturing + Healthcare",
      subtitle: "Industrial ops · HIPAA aware",
    },
    {
      Icon: Sparkles,
      title: "Downtown + Third Ward",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Sacramento (Capital Region) -------- */
export const sacramentoMapConfig = {
  id: "sacramento",
  region: "Capital Region",
  subtitle: "Downtown · Midtown · Natomas · Roseville",
  searchPlaceholder: "Sacramento · Capital Region",
  accentColor: "#F59E0B",
  waterAreas: [
    // Sacramento River (stylized west/north)
    { d: "M100,0 Q120,80 110,160 Q100,220 130,280 L95,280 Q70,200 85,120 Q90,50 100,0 Z", opacity: 0.08 },
  ],
  roads: [
    // I-5 (north-south)
    "M170,0 Q165,140 180,280",
    // I-80 (west-east)
    "M0,120 Q140,110 220,115 T400,110",
    // US-50 (east corridor)
    "M180,160 Q260,155 360,150",
  ],
  districts: [
    { x: 190, y: 155, text: "Downtown", bold: true },
    { x: 220, y: 145, text: "Midtown", bold: true },
    { x: 180, y: 90, text: "Natomas" },
    { x: 300, y: 80, text: "Roseville" },
    { x: 320, y: 130, text: "Folsom" },
    { x: 130, y: 150, text: "West Sacramento" },
  ],
  pins: [
    { x: 192, y: 152, type: "hq", label: "Downtown" },
    { x: 220, y: 142, type: "client", label: "Midtown" },
    { x: 180, y: 87, type: "client", label: "Natomas" },
    { x: 300, y: 77, type: "client", label: "Roseville" },
    { x: 320, y: 127, type: "client" },
    { x: 130, y: 147, type: "client" },
  ],
  stats: [
    { value: "19+", label: "Sac clients" },
    { value: "Gov.", label: "+ Agtech" },
    { value: "10+", label: "Local engineers" },
    { value: "PT", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "BrokerBlocks",
    "Open Tech Alliance",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Government + Healthcare",
      subtitle: "Civic · HIPAA aware",
    },
    {
      Icon: Brain,
      title: "Downtown + Midtown",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- St. Louis (Missouri) -------- */
export const stLouisMapConfig = {
  id: "st-louis",
  region: "St. Louis Metro",
  subtitle: "Downtown · Cortex · Clayton · Central West End",
  searchPlaceholder: "St. Louis · MO / Metro East",
  accentColor: "#EC4899",
  waterAreas: [
    // Mississippi River (stylized east edge)
    { d: "M340,0 Q370,90 355,170 Q345,230 380,280 L400,280 L400,0 Z", opacity: 0.1 },
  ],
  roads: [
    // I-64 / Highway 40 (west-east)
    "M0,150 Q140,140 220,145 T340,140",
    // I-70 (northwest-southeast toward downtown)
    "M40,40 Q140,100 220,145 Q280,180 340,200",
    // I-44 (southwest approach)
    "M60,260 Q160,210 240,160",
  ],
  districts: [
    { x: 250, y: 150, text: "Downtown", bold: true },
    { x: 200, y: 130, text: "Central West End", bold: true },
    { x: 150, y: 145, text: "Clayton" },
    { x: 220, y: 165, text: "Cortex" },
    { x: 260, y: 190, text: "Soulard" },
    { x: 230, y: 200, text: "The Hill" },
  ],
  pins: [
    { x: 252, y: 147, type: "hq", label: "Downtown" },
    { x: 200, y: 127, type: "client", label: "Central West End" },
    { x: 150, y: 142, type: "client", label: "Clayton" },
    { x: 220, y: 162, type: "client", label: "Cortex" },
    { x: 260, y: 187, type: "client" },
    { x: 230, y: 197, type: "client" },
  ],
  stats: [
    { value: "21+", label: "STL clients" },
    { value: "Health", label: "+ Logistics" },
    { value: "11+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "Hyperlogs",
    "FiscalNote",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Healthcare + Logistics",
      subtitle: "HIPAA aware · Corridor ops",
    },
    {
      Icon: MapPin,
      title: "Downtown + Cortex",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Cincinnati (Tri-State) -------- */
export const cincinnatiMapConfig = {
  id: "cincinnati",
  region: "Cincinnati Tri-State",
  subtitle: "Downtown · OTR · Covington · Hyde Park",
  searchPlaceholder: "Cincinnati · OH / NKY",
  accentColor: "#8B5CF6",
  waterAreas: [
    // Ohio River (stylized south edge)
    { d: "M0,210 Q120,195 220,210 Q320,225 400,205 L400,250 Q300,260 200,245 Q100,235 0,250 Z", opacity: 0.1 },
  ],
  roads: [
    // I-71 / I-75 corridor (north-south)
    "M200,0 Q195,120 210,210",
    // I-275 outer belt (partial)
    "M60,40 Q30,140 80,220 Q200,270 320,230 Q380,150 340,50",
    // Cross-town toward Hyde Park / Oakley
    "M210,160 Q280,150 360,140",
  ],
  districts: [
    { x: 200, y: 170, text: "Downtown", bold: true },
    { x: 210, y: 145, text: "Over-the-Rhine", bold: true },
    { x: 190, y: 205, text: "Covington" },
    { x: 280, y: 140, text: "Hyde Park" },
    { x: 300, y: 160, text: "Oakley" },
    { x: 320, y: 100, text: "Blue Ash" },
  ],
  pins: [
    { x: 202, y: 167, type: "hq", label: "Downtown" },
    { x: 210, y: 142, type: "client", label: "Over-the-Rhine" },
    { x: 190, y: 202, type: "client", label: "Covington" },
    { x: 280, y: 137, type: "client", label: "Hyde Park" },
    { x: 300, y: 157, type: "client" },
    { x: 320, y: 97, type: "client" },
  ],
  stats: [
    { value: "22+", label: "CVG clients" },
    { value: "CPG", label: "+ Healthcare" },
    { value: "11+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Online Rewards",
    "BrokerBlocks",
    "CrowdKeep",
    "American Water Works Assoc.",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "CPG + Insurance",
      subtitle: "Brand apps · Compliance",
    },
    {
      Icon: ShieldCheck,
      title: "Downtown + OTR",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Jacksonville (Northeast Florida) -------- */
export const jacksonvilleMapConfig = {
  id: "jacksonville",
  region: "Northeast Florida",
  subtitle: "Downtown · Beaches · Riverside · Town Center",
  searchPlaceholder: "Jacksonville · NE Florida",
  accentColor: "#00AEEF",
  waterAreas: [
    // St. Johns River (stylized diagonal)
    { d: "M180,0 Q200,80 170,150 Q140,210 160,280 L120,280 Q100,200 130,120 Q150,50 180,0 Z", opacity: 0.1 },
  ],
  roads: [
    // I-95 (north-south)
    "M210,0 Q205,140 220,280",
    // I-10 (west-east through downtown)
    "M0,160 Q140,150 220,155 T400,150",
    // Beach / Atlantic corridor
    "M240,120 Q300,130 380,140",
  ],
  districts: [
    { x: 200, y: 155, text: "Downtown", bold: true },
    { x: 240, y: 190, text: "Southside", bold: true },
    { x: 330, y: 145, text: "Beaches" },
    { x: 160, y: 140, text: "Riverside" },
    { x: 280, y: 170, text: "Town Center" },
    { x: 250, y: 230, text: "Mandarin" },
  ],
  pins: [
    { x: 202, y: 152, type: "hq", label: "Downtown" },
    { x: 240, y: 187, type: "client", label: "Southside" },
    { x: 330, y: 142, type: "client", label: "Beaches" },
    { x: 160, y: 137, type: "client", label: "Riverside" },
    { x: 280, y: 167, type: "client" },
    { x: 250, y: 227, type: "client" },
  ],
  stats: [
    { value: "20+", label: "Jax clients" },
    { value: "Log.", label: "+ Finance" },
    { value: "10+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "SCS Engineers",
    "Open Tech Alliance",
    "TruthQuotient",
    "Hyperlogs",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "Logistics + Defense",
      subtitle: "Port ops · Secure builds",
    },
    {
      Icon: Brain,
      title: "Downtown + Beaches",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Baltimore (Central Maryland) -------- */
export const baltimoreMapConfig = {
  id: "baltimore",
  region: "Central Maryland",
  subtitle: "Downtown · Inner Harbor · Harbor East · Canton",
  searchPlaceholder: "Baltimore · Central MD",
  accentColor: "#00AEEF",
  waterAreas: [
    // Inner Harbor / Patapsco (stylized south-central)
    { d: "M140,180 Q200,170 250,185 Q300,200 340,190 L340,250 Q260,270 180,255 Q120,240 140,180 Z", opacity: 0.1 },
  ],
  roads: [
    // I-95 (southwest-northeast)
    "M40,240 Q160,180 280,120 Q340,90 400,70",
    // I-83 / Jones Falls (north-south into downtown)
    "M210,0 Q205,100 215,175",
    // Beltway / I-695 partial arc
    "M60,40 Q20,140 80,230 Q200,280 340,220 Q390,140 350,40",
  ],
  districts: [
    { x: 200, y: 165, text: "Downtown", bold: true },
    { x: 220, y: 195, text: "Inner Harbor", bold: true },
    { x: 260, y: 175, text: "Harbor East" },
    { x: 280, y: 200, text: "Fells Point" },
    { x: 300, y: 185, text: "Canton" },
    { x: 120, y: 100, text: "Columbia" },
  ],
  pins: [
    { x: 202, y: 162, type: "hq", label: "Downtown" },
    { x: 220, y: 192, type: "client", label: "Inner Harbor" },
    { x: 260, y: 172, type: "client", label: "Harbor East" },
    { x: 280, y: 197, type: "client", label: "Fells Point" },
    { x: 300, y: 182, type: "client" },
    { x: 120, y: 97, type: "client" },
  ],
  stats: [
    { value: "24+", label: "BWI clients" },
    { value: "Health", label: "+ Biotech" },
    { value: "12+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "PAN Foundation",
    "FiscalNote",
    "Yes! Communities",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Healthcare + Biotech",
      subtitle: "HIPAA-aware · Research ops",
    },
    {
      Icon: MapPin,
      title: "Harbor + Columbia",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Louisville (Kentuckiana) -------- */
export const louisvilleMapConfig = {
  id: "louisville",
  region: "Kentuckiana",
  subtitle: "Downtown · NuLu · Highlands · East End",
  searchPlaceholder: "Louisville · KY / S. Indiana",
  accentColor: "#10B981",
  waterAreas: [
    // Ohio River (stylized north edge)
    { d: "M0,40 Q120,25 220,40 Q320,55 400,35 L400,90 Q300,100 200,85 Q100,75 0,90 Z", opacity: 0.1 },
  ],
  roads: [
    // I-65 (north-south)
    "M210,0 Q205,120 215,280",
    // I-64 (west-east)
    "M0,150 Q140,145 220,150 T400,145",
    // Gene Snyder / East End corridor
    "M240,160 Q300,140 380,120",
  ],
  districts: [
    { x: 200, y: 145, text: "Downtown", bold: true },
    { x: 240, y: 155, text: "NuLu", bold: true },
    { x: 260, y: 175, text: "Highlands" },
    { x: 320, y: 130, text: "East End" },
    { x: 300, y: 160, text: "St. Matthews" },
    { x: 340, y: 180, text: "Jeffersontown" },
  ],
  pins: [
    { x: 202, y: 142, type: "hq", label: "Downtown" },
    { x: 240, y: 152, type: "client", label: "NuLu" },
    { x: 260, y: 172, type: "client", label: "Highlands" },
    { x: 320, y: 127, type: "client", label: "East End" },
    { x: 300, y: 157, type: "client" },
    { x: 340, y: 177, type: "client" },
  ],
  stats: [
    { value: "21+", label: "SDF clients" },
    { value: "Log.", label: "+ Healthcare" },
    { value: "11+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "Hyperlogs",
    "Online Rewards",
    "CrowdKeep",
    "American Water Works Assoc.",
  ],
  badges: [
    {
      Icon: Activity,
      title: "Healthcare + Logistics",
      subtitle: "Clinical · Hub ops",
    },
    {
      Icon: Sparkles,
      title: "Downtown + NuLu",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Oklahoma City (OKC Metro) -------- */
export const oklahomaCityMapConfig = {
  id: "oklahoma-city",
  region: "OKC Metro",
  subtitle: "Downtown · Midtown · Bricktown · Edmond",
  searchPlaceholder: "Oklahoma City · OK Metro",
  accentColor: "#F59E0B",
  waterAreas: [
    // Oklahoma River (stylized south band)
    { d: "M40,200 Q140,190 220,205 Q300,220 380,200 L380,250 Q280,265 180,250 Q80,240 40,250 Z", opacity: 0.08 },
  ],
  roads: [
    // I-35 (north-south)
    "M220,0 Q215,140 225,280",
    // I-40 (west-east)
    "M0,160 Q140,155 220,160 T400,155",
    // Broadway Extension / Edmond corridor
    "M210,160 Q200,80 190,20",
  ],
  districts: [
    { x: 200, y: 155, text: "Downtown", bold: true },
    { x: 190, y: 125, text: "Midtown", bold: true },
    { x: 240, y: 170, text: "Bricktown" },
    { x: 200, y: 60, text: "Edmond" },
    { x: 280, y: 230, text: "Norman" },
    { x: 140, y: 100, text: "Quail Creek" },
  ],
  pins: [
    { x: 202, y: 152, type: "hq", label: "Downtown" },
    { x: 190, y: 122, type: "client", label: "Midtown" },
    { x: 240, y: 167, type: "client", label: "Bricktown" },
    { x: 200, y: 57, type: "client", label: "Edmond" },
    { x: 280, y: 227, type: "client" },
    { x: 140, y: 97, type: "client" },
  ],
  stats: [
    { value: "19+", label: "OKC clients" },
    { value: "Energy", label: "+ Aerospace" },
    { value: "10+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "BrokerBlocks",
    "SCS Engineers",
    "Open Tech Alliance",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: Cpu,
      title: "Energy + Aerospace",
      subtitle: "Field ops · Secure builds",
    },
    {
      Icon: Brain,
      title: "Downtown + Edmond",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Las Vegas (Southern Nevada) -------- */
export const lasVegasMapConfig = {
  id: "las-vegas",
  region: "Southern Nevada",
  subtitle: "Strip · Downtown · Summerlin · Henderson",
  searchPlaceholder: "Las Vegas · So. Nevada",
  accentColor: "#EC4899",
  waterAreas: [
    // Lake Mead / Colorado hint (southeast)
    { d: "M300,220 Q360,200 400,230 L400,280 L280,280 Z", opacity: 0.06 },
  ],
  roads: [
    // I-15 (southwest-northeast)
    "M80,280 Q160,160 240,80 Q300,40 360,10",
    // I-215 belt partial
    "M60,60 Q40,160 100,240 Q200,280 320,220 Q380,140 340,40",
    // Summerlin Parkway-ish west corridor
    "M40,120 Q100,110 180,130",
  ],
  districts: [
    { x: 210, y: 150, text: "Strip", bold: true },
    { x: 200, y: 110, text: "Downtown", bold: true },
    { x: 100, y: 100, text: "Summerlin" },
    { x: 280, y: 190, text: "Henderson" },
    { x: 160, y: 180, text: "Airport" },
    { x: 250, y: 90, text: "North LV" },
  ],
  pins: [
    { x: 212, y: 147, type: "hq", label: "Strip" },
    { x: 200, y: 107, type: "client", label: "Downtown" },
    { x: 100, y: 97, type: "client", label: "Summerlin" },
    { x: 280, y: 187, type: "client", label: "Henderson" },
    { x: 160, y: 177, type: "client" },
    { x: 250, y: 87, type: "client" },
  ],
  stats: [
    { value: "18+", label: "Vegas clients" },
    { value: "Hospitality", label: "+ Venues" },
    { value: "10+", label: "Local engineers" },
    { value: "PT", label: "Time aligned" },
  ],
  recentClients: [
    "Yes! Communities",
    "BrokerBlocks",
    "Toastmasters International",
    "Open Tech Alliance",
  ],
  badges: [
    {
      Icon: Sparkles,
      title: "Hospitality + Venues",
      subtitle: "Guest ops · Peak nights",
    },
    {
      Icon: MapPin,
      title: "Strip + Summerlin",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- San Antonio (South Texas) -------- */
export const sanAntonioMapConfig = {
  id: "san-antonio",
  region: "South Texas",
  subtitle: "Downtown · Pearl · Stone Oak · Southtown",
  searchPlaceholder: "San Antonio · South TX",
  accentColor: "#10B981",
  waterAreas: [
    // River Walk hint (central meander)
    { d: "M180,120 Q200,150 190,180 Q175,210 210,230 L190,250 Q150,220 160,180 Q170,140 180,120 Z", opacity: 0.08 },
  ],
  roads: [
    // I-35 (northeast-southwest)
    "M320,0 Q250,120 200,200 Q150,260 80,280",
    // I-10 (west-east)
    "M0,160 Q140,150 220,155 T400,150",
    // Loop 1604 partial arc
    "M80,40 Q30,140 90,240 Q200,290 340,220 Q390,120 320,40",
  ],
  districts: [
    { x: 200, y: 155, text: "Downtown", bold: true },
    { x: 210, y: 120, text: "Pearl", bold: true },
    { x: 230, y: 190, text: "Southtown" },
    { x: 240, y: 70, text: "Stone Oak" },
    { x: 140, y: 100, text: "Medical Ctr" },
    { x: 300, y: 160, text: "Fort Sam" },
  ],
  pins: [
    { x: 202, y: 152, type: "hq", label: "Downtown" },
    { x: 210, y: 117, type: "client", label: "Pearl" },
    { x: 230, y: 187, type: "client", label: "Southtown" },
    { x: 240, y: 67, type: "client", label: "Stone Oak" },
    { x: 140, y: 97, type: "client" },
    { x: 300, y: 157, type: "client" },
  ],
  stats: [
    { value: "22+", label: "SA clients" },
    { value: "Health", label: "+ Cyber" },
    { value: "12+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "SmileML",
    "PAN Foundation",
    "SCS Engineers",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Healthcare + Cyber",
      subtitle: "HIPAA-aware · Secure builds",
    },
    {
      Icon: MapPin,
      title: "Pearl + Downtown",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Richmond (Central Virginia) -------- */
export const richmondMapConfig = {
  id: "richmond",
  region: "Central Virginia",
  subtitle: "Downtown · Scott's Addition · Short Pump",
  searchPlaceholder: "Richmond · Central VA",
  accentColor: "#00AEEF",
  waterAreas: [
    // James River (south band)
    { d: "M40,200 Q140,185 220,200 Q300,215 380,195 L380,250 Q280,265 180,250 Q80,240 40,250 Z", opacity: 0.1 },
  ],
  roads: [
    // I-95 (north-south)
    "M210,0 Q205,140 220,280",
    // I-64 (west-east)
    "M0,140 Q140,135 220,140 T400,135",
    // Powhite / west end corridor
    "M40,100 Q120,120 200,145",
  ],
  districts: [
    { x: 200, y: 145, text: "Downtown", bold: true },
    { x: 170, y: 115, text: "Scott's Addition", bold: true },
    { x: 100, y: 100, text: "Short Pump" },
    { x: 230, y: 170, text: "Shockoe" },
    { x: 160, y: 160, text: "The Fan" },
    { x: 280, y: 100, text: "Innsbrook" },
  ],
  pins: [
    { x: 202, y: 142, type: "hq", label: "Downtown" },
    { x: 170, y: 112, type: "client", label: "Scott's Addition" },
    { x: 100, y: 97, type: "client", label: "Short Pump" },
    { x: 230, y: 167, type: "client", label: "Shockoe" },
    { x: 160, y: 157, type: "client" },
    { x: 280, y: 97, type: "client" },
  ],
  stats: [
    { value: "20+", label: "RVA clients" },
    { value: "Finance", label: "+ Healthcare" },
    { value: "14+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "AT&T Government Solutions",
    "PAN Foundation",
    "BrokerBlocks",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Finance + Healthcare",
      subtitle: "Compliance-minded delivery",
    },
    {
      Icon: MapPin,
      title: "RVA + DMV continuity",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- New Orleans (Southeast Louisiana) -------- */
export const newOrleansMapConfig = {
  id: "new-orleans",
  region: "Southeast Louisiana",
  subtitle: "CBD · French Quarter · Metairie · Uptown",
  searchPlaceholder: "New Orleans · Gulf Coast",
  accentColor: "#F59E0B",
  waterAreas: [
    // Mississippi River crescent (south band)
    { d: "M40,210 Q120,180 200,205 Q280,230 380,200 L380,280 L40,280 Z", opacity: 0.1 },
    // Lake Pontchartrain hint (north edge)
    { d: "M0,0 L400,0 L400,45 Q200,70 0,45 Z", opacity: 0.08 },
  ],
  roads: [
    // I-10 (west-east)
    "M0,130 Q140,120 220,130 T400,120",
    // I-610 shortcut
    "M120,110 Q220,100 320,115",
    // Pontchartrain Expressway (north-south to river)
    "M210,60 Q205,150 220,210",
  ],
  districts: [
    { x: 205, y: 150, text: "CBD", bold: true },
    { x: 245, y: 175, text: "French Quarter", bold: true },
    { x: 170, y: 170, text: "Uptown" },
    { x: 110, y: 110, text: "Metairie" },
    { x: 60, y: 95, text: "Kenner" },
    { x: 300, y: 120, text: "New Orleans East" },
  ],
  pins: [
    { x: 207, y: 147, type: "hq", label: "CBD" },
    { x: 245, y: 172, type: "client", label: "French Quarter" },
    { x: 170, y: 167, type: "client", label: "Uptown" },
    { x: 110, y: 107, type: "client", label: "Metairie" },
    { x: 60, y: 92, type: "client" },
    { x: 300, y: 117, type: "client" },
  ],
  stats: [
    { value: "18+", label: "NOLA clients" },
    { value: "Energy", label: "+ Hospitality" },
    { value: "10+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "SCS Engineers",
    "PAN Foundation",
    "FiscalNote",
    "TruthQuotient",
  ],
  badges: [
    {
      Icon: MapPin,
      title: "CBD + French Quarter",
      subtitle: "On-site weekly",
    },
    {
      Icon: Activity,
      title: "Energy + Hospitality",
      subtitle: "Field & guest experience",
    },
  ],
};

/* -------- Omaha (Eastern Nebraska) -------- */
export const omahaMapConfig = {
  id: "omaha",
  region: "Eastern Nebraska",
  subtitle: "Downtown · Old Market · Aksarben · West Omaha",
  searchPlaceholder: "Omaha · Silicon Prairie",
  accentColor: "#10B981",
  waterAreas: [
    // Missouri River (east border)
    { d: "M300,0 Q330,80 315,160 Q300,240 340,280 L400,280 L400,0 Z", opacity: 0.09 },
  ],
  roads: [
    // I-80 (west-east)
    "M0,180 Q140,175 240,180 T400,175",
    // I-480 / downtown connector
    "M240,120 Q280,150 300,190",
    // I-680 north loop
    "M60,60 Q180,40 300,70",
  ],
  districts: [
    { x: 270, y: 150, text: "Downtown", bold: true },
    { x: 285, y: 180, text: "Old Market", bold: true },
    { x: 200, y: 165, text: "Aksarben" },
    { x: 150, y: 130, text: "Midtown" },
    { x: 90, y: 150, text: "West Omaha" },
    { x: 330, y: 200, text: "Council Bluffs" },
  ],
  pins: [
    { x: 272, y: 147, type: "hq", label: "Downtown" },
    { x: 285, y: 177, type: "client", label: "Old Market" },
    { x: 200, y: 162, type: "client", label: "Aksarben" },
    { x: 150, y: 127, type: "client", label: "Midtown" },
    { x: 90, y: 147, type: "client" },
    { x: 330, y: 197, type: "client" },
  ],
  stats: [
    { value: "16+", label: "Omaha clients" },
    { value: "Finance", label: "+ Agtech" },
    { value: "10+", label: "Local engineers" },
    { value: "CT", label: "Time aligned" },
  ],
  recentClients: [
    "FiscalNote",
    "PAN Foundation",
    "BrokerBlocks",
    "SCS Engineers",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Finance + Insurance",
      subtitle: "Compliance-minded delivery",
    },
    {
      Icon: MapPin,
      title: "Downtown + Old Market",
      subtitle: "On-site weekly",
    },
  ],
};

/* -------- Hartford (Central Connecticut) -------- */
export const hartfordMapConfig = {
  id: "hartford",
  region: "Central Connecticut",
  subtitle: "Downtown · Asylum Hill · West Hartford · Farmington",
  searchPlaceholder: "Hartford · Central CT",
  accentColor: "#00AEEF",
  waterAreas: [
    // Connecticut River (north-south, east of center)
    { d: "M250,0 Q235,80 250,160 Q265,240 255,280 L300,280 Q290,160 300,80 L300,0 Z", opacity: 0.1 },
  ],
  roads: [
    // I-84 (west-east)
    "M0,140 Q120,135 220,145 T400,135",
    // I-91 (north-south)
    "M235,0 Q230,140 245,280",
    // Route 2 (southeast)
    "M245,160 Q300,200 360,240",
  ],
  districts: [
    { x: 220, y: 145, text: "Downtown", bold: true },
    { x: 175, y: 120, text: "Asylum Hill", bold: true },
    { x: 110, y: 130, text: "West Hartford" },
    { x: 60, y: 160, text: "Farmington" },
    { x: 290, y: 150, text: "East Hartford" },
    { x: 150, y: 70, text: "Bloomfield" },
  ],
  pins: [
    { x: 222, y: 142, type: "hq", label: "Downtown" },
    { x: 175, y: 117, type: "client", label: "Asylum Hill" },
    { x: 110, y: 127, type: "client", label: "West Hartford" },
    { x: 60, y: 157, type: "client", label: "Farmington" },
    { x: 290, y: 147, type: "client" },
    { x: 150, y: 67, type: "client" },
  ],
  stats: [
    { value: "17+", label: "Hartford clients" },
    { value: "Insurance", label: "+ Finance" },
    { value: "12+", label: "Local engineers" },
    { value: "ET", label: "Time aligned" },
  ],
  recentClients: [
    "PAN Foundation",
    "FiscalNote",
    "BrokerBlocks",
    "Toastmasters International",
  ],
  badges: [
    {
      Icon: ShieldCheck,
      title: "Insurance + Finance",
      subtitle: "Compliance-minded delivery",
    },
    {
      Icon: MapPin,
      title: "Downtown + Asylum Hill",
      subtitle: "On-site weekly",
    },
  ],
};

export const locationMapConfigs = {
  "app-development-company-washington-dc": dcMapConfig,
  "app-development-company-virginia": vaMapConfig,
  "app-development-company-maryland": mdMapConfig,
  "app-development-company-new-york": nyMapConfig,
  "app-development-company-boston": bostonMapConfig,
  "app-development-company-austin": austinMapConfig,
  "app-development-company-los-angeles": laMapConfig,
  "app-development-company-san-francisco": sfMapConfig,
  "app-development-company-chicago": chicagoMapConfig,
  "app-development-company-dallas": dallasMapConfig,
  "app-development-company-atlanta": atlantaMapConfig,
  "app-development-company-phoenix": phoenixMapConfig,
  "app-development-company-houston": houstonMapConfig,
  "app-development-company-san-diego": sanDiegoMapConfig,
  "app-development-company-philadelphia": philadelphiaMapConfig,
  "app-development-company-nashville": nashvilleMapConfig,
  "app-development-company-portland": portlandMapConfig,
  "app-development-company-minneapolis": minneapolisMapConfig,
  "app-development-company-charlotte": charlotteMapConfig,
  "app-development-company-salt-lake-city": saltLakeCityMapConfig,
  "app-development-company-raleigh": raleighMapConfig,
  "app-development-company-tampa": tampaMapConfig,
  "app-development-company-detroit": detroitMapConfig,
  "app-development-company-orlando": orlandoMapConfig,
  "app-development-company-columbus": columbusMapConfig,
  "app-development-company-indianapolis": indianapolisMapConfig,
  "app-development-company-pittsburgh": pittsburghMapConfig,
  "app-development-company-kansas-city": kansasCityMapConfig,
  "app-development-company-cleveland": clevelandMapConfig,
  "app-development-company-milwaukee": milwaukeeMapConfig,
  "app-development-company-sacramento": sacramentoMapConfig,
  "app-development-company-st-louis": stLouisMapConfig,
  "app-development-company-cincinnati": cincinnatiMapConfig,
  "app-development-company-jacksonville": jacksonvilleMapConfig,
  "app-development-company-baltimore": baltimoreMapConfig,
  "app-development-company-louisville": louisvilleMapConfig,
  "app-development-company-oklahoma-city": oklahomaCityMapConfig,
  "app-development-company-las-vegas": lasVegasMapConfig,
  "app-development-company-san-antonio": sanAntonioMapConfig,
  "app-development-company-richmond": richmondMapConfig,
  "app-development-company-new-orleans": newOrleansMapConfig,
  "app-development-company-omaha": omahaMapConfig,
  "app-development-company-hartford": hartfordMapConfig,
};
